import { useEffect, useMemo, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import ContactDrawer from "./ContactDrawer";
import RemindersPanel from "./RemindersPanel";
import {
  ACTIVITY_TYPE_LABELS,
  PIPELINE_LABELS,
  PIPELINE_STATUSES,
  PROTOCOL_LABELS,
  PROVENANCE_LABELS,
  todayIso,
} from "./labels";

const VIEWS = [
  { v: "attivi", l: "ATTIVI" },
  { v: "archiviati", l: "ARCHIVIATI" },
  { v: "cestino", l: "CESTINO (30 GIORNI)" },
];

const fmtDay = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
  } catch {
    return iso || "";
  }
};

function NextAppointments({ bookings, compact }) {
  const today = todayIso();
  const next = (bookings || [])
    .filter((b) => b.status === "confermata" && b.date && b.date >= today)
    .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time))
    .slice(0, compact ? 3 : 5);
  if (!next.length) return null;
  return (
    <div className={compact ? "" : "border border-[#c9a45c]/25 bg-[#101010] p-4"} data-testid="next-appointments">
      {!compact && <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-gold-light">Prossimi appuntamenti</p>}
      {compact && <p className="text-[8px] font-bold uppercase tracking-[0.14em] text-[#8f8f8f]">Appuntamenti</p>}
      <div className="mt-2 flex flex-wrap gap-2">
        {next.map((b) => (
          <a
            key={b.id}
            href={b.meet_link || "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="border border-[#d4af37]/40 bg-[#d4af37]/8 px-2.5 py-1.5 text-[10px] text-cream transition-colors hover:bg-[#d4af37]/15"
            data-testid={`next-appointment-${b.id}`}
          >
            <span className="font-bold text-gold-light">{fmtDay(b.date)} · {b.time}</span>
            <span className="ml-2">{b.name}</span>
            {!compact && <span className="ml-2 text-[9px] uppercase tracking-[0.1em] text-[#a8a5a0]">{PROTOCOL_LABELS[b.protocol_interest] || ""}</span>}
          </a>
        ))}
      </div>
    </div>
  );
}

function NextActivities({ contacts, onOpen, compact }) {
  const items = (contacts || [])
    .filter((c) => c.next_activity)
    .sort((a, b) =>
      `${a.next_activity.due_date}${a.next_activity.due_time}`.localeCompare(`${b.next_activity.due_date}${b.next_activity.due_time}`)
    )
    .slice(0, compact ? 3 : 5);
  if (!items.length) return null;
  return (
    <div className={compact ? "" : "mt-3 border border-[#c9a45c]/25 bg-[#101010] p-4"} data-testid="next-activities">
      <p className="text-[8px] font-bold uppercase tracking-[0.14em] text-[#8f8f8f]">Attività da svolgere</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {items.map((c) => (
          <button
            key={c.id}
            onClick={() => onOpen(c.id)}
            className="border border-[#c9a45c]/40 bg-[#c9a45c]/8 px-2.5 py-1.5 text-[10px] text-cream transition-colors hover:bg-[#c9a45c]/15"
            data-testid={`next-activity-${c.id}`}
          >
            <span className="font-bold text-gold-light">
              {ACTIVITY_TYPE_LABELS[c.next_activity.type] || c.next_activity.type} · {fmtDay(c.next_activity.due_date)} ore {c.next_activity.due_time}
            </span>
            <span className="ml-2">{c.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ContactRow({ contact, onOpen, onPipeline }) {
  const next = (contact.bookings || []).find((b) => b.status === "confermata" && b.date >= todayIso());
  return (
    <div
      className="border border-[#c9a45c]/18 bg-[#101010] p-5 transition-colors hover:border-[#c9a45c]/40"
      data-testid={`contact-row-${contact.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <button onClick={() => onOpen(contact.id)} className="min-w-0 text-left" data-testid={`contact-open-${contact.id}`}>
          <p className="font-serif text-lg text-cream hover:text-gold-light" data-testid={`contact-name-${contact.id}`}>{contact.name}</p>
          <p className="mt-1 font-mono text-[10px] tracking-[0.12em] text-[#929292]">
            aggiornato {fmtDay(contact.updated_at || contact.created_at)}
            {contact.note_count ? ` · ${contact.note_count} note` : ""}
            {contact.lead_count ? ` · ${contact.lead_count} richiest${contact.lead_count === 1 ? "a" : "e"}` : ""}
          </p>
        </button>
        <select
          className="field !w-auto !py-2 !text-[11px]"
          value={contact.pipeline_status}
          onChange={(e) => onPipeline(contact.id, e.target.value)}
          data-testid={`contact-pipeline-${contact.id}`}
        >
          {PIPELINE_STATUSES.map((s) => (
            <option key={s} value={s}>{PIPELINE_LABELS[s]}</option>
          ))}
        </select>
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-[12px] text-[#c9c6bf]">
        {contact.is_demo && (
          <span className="border border-[#d4af37]/60 bg-[#d4af37]/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-gold-light" data-testid={`contact-demo-badge-${contact.id}`}>
            Demo
          </span>
        )}
        <span className="border border-[#c9a45c]/40 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-gold-light" data-testid={`contact-protocol-${contact.id}`}>
          {PROTOCOL_LABELS[contact.protocol_interest] || contact.protocol_interest}
        </span>
        <span className="border border-[#c9a45c]/25 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-[#c9c6bf]" data-testid={`contact-provenance-${contact.id}`}>
          da {PROVENANCE_LABELS[contact.provenance] || contact.provenance}
        </span>
        {contact.phone && <span>{contact.phone}</span>}
        {contact.email && <span className="truncate">{contact.email}</span>}
        {next && (
          <span className="text-gold-light" data-testid={`contact-next-${contact.id}`}>
            Prossima sessione: {fmtDay(next.date)} ore {next.time}
          </span>
        )}
        {contact.next_activity && (
          <span className="text-[#e1c47a]" data-testid={`contact-activity-${contact.id}`}>
            Prossima attività: {ACTIVITY_TYPE_LABELS[contact.next_activity.type] || contact.next_activity.type} {fmtDay(contact.next_activity.due_date)} ore {contact.next_activity.due_time}
          </span>
        )}
      </div>
    </div>
  );
}

export default function ContactsPanel() {
  const [contacts, setContacts] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [view, setView] = useState("attivi");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);
  const [focusActivityId, setFocusActivityId] = useState(null);

  const openContact = (id, activityId = null) => {
    setFocusActivityId(activityId);
    setOpenId(id);
  };

  const load = () => {
    api
      .get("/contacts", { params: { view } })
      .then(({ data }) => setContacts(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };

  useEffect(load, [view]);
  useEffect(() => {
    api
      .get("/bookings")
      .then(({ data }) => setBookings(data))
      .catch(() => {});
  }, []);

  const onPipeline = async (id, status) => {
    try {
      const { data } = await api.patch(`/contacts/${id}`, { pipeline_status: status });
      setContacts((cs) => (cs || []).map((c) => (c.id === id ? { ...c, ...data } : c)));
      toast.success("Percorso clienti aggiornato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const counts = useMemo(() => {
    const map = {};
    (contacts || []).filter((c) => !c.is_demo).forEach((c) => {
      map[c.pipeline_status] = (map[c.pipeline_status] || 0) + 1;
    });
    return map;
  }, [contacts]);

  const demoIds = useMemo(() => new Set((contacts || []).filter((c) => c.is_demo).map((c) => c.id)), [contacts]);
  const realBookings = useMemo(() => (bookings || []).filter((b) => !demoIds.has(b.contact_id)), [bookings, demoIds]);
  const activeContacts = useMemo(() => (contacts || []).filter((c) => !c.is_demo), [contacts]);

  const filtered = (contacts || []).filter((c) => {
    if (statusFilter && c.pipeline_status !== statusFilter) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const hay = `${c.name || ""} ${c.email || ""} ${c.phone || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="pb-16" data-testid="contacts-panel">
      <RemindersPanel onOpenContact={openContact} />

      <div className="mt-4 border border-[#c9a45c]/25 bg-[#101010] p-4" data-testid="highlights-panel">
        <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-gold-light">In primo piano</p>
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          <NextAppointments bookings={realBookings} compact />
          <NextActivities contacts={activeContacts} onOpen={openContact} compact />
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2" data-testid="pipeline-counters">
        {PIPELINE_STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(statusFilter === s ? "" : s)}
            className={`border px-2.5 py-1.5 text-[8px] font-bold tracking-[0.12em] transition-colors duration-200 ${
              statusFilter === s
                ? "border-gold bg-[#c9a45c]/12 text-gold-light"
                : "border-[#c9a45c]/25 text-[#929292] hover:text-cream"
            }`}
            data-testid={`pipeline-filter-${s}`}
          >
            {PIPELINE_LABELS[s].toUpperCase()} · {view === "attivi" ? counts[s] || 0 : "—"}
          </button>
        ))}
        {statusFilter && (
          <button onClick={() => setStatusFilter("")} className="px-3 py-2 text-[9px] font-bold tracking-[0.12em] text-[#929292] hover:text-cream" data-testid="pipeline-filter-reset">
            ✕ Azzera filtro
          </button>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="contacts-controls">
        {VIEWS.map((v) => (
          <button
            key={v.v}
            onClick={() => setView(v.v)}
            className={`border px-3 py-2 text-[9px] font-bold tracking-[0.14em] transition-colors duration-200 ${
              view === v.v ? "border-gold bg-[#c9a45c]/12 text-gold-light" : "border-[#c9a45c]/25 text-[#929292] hover:text-cream"
            }`}
            data-testid={`contacts-view-${v.v}`}
          >
            {v.l}
          </button>
        ))}
        <div className="relative ml-auto">
          <Search size={12} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#777]" />
          <input
            className="field !w-[240px] !py-2 !pl-8 !text-[12px]"
            placeholder="Cerca nome, email o telefono…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="contacts-search"
          />
        </div>
      </div>

      <div className="mt-6 grid gap-3" data-testid="contacts-list">
        {contacts === null && (
          <div className="flex items-center gap-3 border border-[#c9a45c]/18 bg-[#101010] p-6 text-[13px] text-[#929292]" data-testid="contacts-loading">
            <Loader2 size={14} className="animate-spin text-gold" /> Caricamento contatti…
          </div>
        )}
        {contacts !== null && filtered.length === 0 && (
          <div className="border border-[#c9a45c]/18 bg-[#101010] p-8 text-center text-[13px] text-[#929292]" data-testid="contacts-empty">
            {view === "cestino"
              ? "Il cestino è vuoto."
              : view === "archiviati"
                ? "Nessun contatto archiviato."
                : "Nessun contatto in questa vista, per ora."}
          </div>
        )}
        {filtered.map((c) => (
          <ContactRow key={c.id} contact={c} onOpen={openContact} onPipeline={onPipeline} />
        ))}
      </div>

      {openId && (
        <ContactDrawer
          contactId={openId}
          focusActivityId={focusActivityId}
          onClose={() => setOpenId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}
